import { z } from "zod";

export const PrinterSchema = z.object({
  id: z.string().optional(),
  serialNumber: z
    .string({ required_error: "Serial Number is required" })
    .min(4, { message: "Serial Number must be 4 digits or more" }),
  active: z.boolean(),
  activeForProductPrice: z.boolean(),
  tag: z
    .string({ required_error: "Tag is required" })
    .min(4, { message: "Tag must be 4 digits or more" }),
  activeForWarehouseLabel: z.boolean(),
  activeForTransferCompleteLabel: z.boolean(),
  activeForOrderItem: z.boolean(),
  activeForManualTransferCompleteLabel: z.boolean(),
  size: z.enum(
    ["Small - 2.25 x 1.25", "Large - 2.25 x 3", "ExtraLarge - 4 x 6"],
    { required_error: "Size is required" },
  ),
});

export const StoreSchema = z
  .object({
    active: z.boolean(),
    displayName: z
      .string({ required_error: "Display Name is required" })
      .min(4, { message: "Display Name must be 4 digits or more" }),
    geolocationLat: z.string().or(z.literal("")).optional(),
    geolocationLon: z.string().or(z.literal("")).optional(),
    shipstationTagIdShipping: z.string().optional(),
    shipstationTagIdDelivery: z.string().optional(),
    shipstationTagIdStorePickup: z.string().optional(),
    shipstationCompanyName: z.string({}),
    zapietLocationIDs: z
      .string()
      .optional()
      .refine(
        (val) =>
          val === undefined ||
          val === "" ||
          val
            .split(",")
            .map((str) => str.trim())
            .every(Boolean),
        { message: "Invalid format, please separate values with commas" },
      ),
  })
  .superRefine((data, ctx) => {
    const latFilled = data.geolocationLat?.trim() !== "";
    const lonFilled = data.geolocationLon?.trim() !== "";

    if (latFilled && !lonFilled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Latitude and longitude must be filled at the same time",
        path: ["geolocationLon"],
      });
    } else if (!latFilled && lonFilled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Latitude and longitude must be filled at the same time",
        path: ["geolocationLat"],
      });
    }

    if (data.active) {
      if (!data.shipstationTagIdShipping) {
        ctx.addIssue({
          path: ["shipstationTagIdShipping"],
          code: z.ZodIssueCode.custom,
          message: "Required",
        });
      }
      if (!data.shipstationTagIdDelivery) {
        ctx.addIssue({
          path: ["shipstationTagIdDelivery"],
          code: z.ZodIssueCode.custom,
          message: "Required",
        });
      }
      if (!data.shipstationTagIdStorePickup) {
        ctx.addIssue({
          path: ["shipstationTagIdStorePickup"],
          code: z.ZodIssueCode.custom,
          message: "Required",
        });
      }
    }
  });

export type TPrinterSchema = z.infer<typeof PrinterSchema>;
export type TStoreSchema = z.infer<typeof StoreSchema>;
